import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { PREP_TOUR_END, PrepTourDefinition, PrepTourDialogContent, PrepTourEvent, PrepTourPhase, PrepTourStep } from './tour.types';
import { validateTourDefinition } from './validate-tour';
import { PrepTourAnchorRegistry } from './tour-anchor-registry.service';
import { PrepGuideRegistry } from './guide-registry.service';
import { PrepTourProgressStore } from './tour-progress.store';

export interface PrepTourStartOptions {
  /** Resume at this step (default 0, or the welcome dialog when defined). */
  fromStep?: number;
  /** Skip the welcome dialog even if the guide has one. */
  skipWelcome?: boolean;
  /** Element to focus when the guide stops (the launcher button). */
  returnFocusTo?: HTMLElement | null;
}

/** How often a predicate step re-checks `satisfied()` (outside the Angular zone). */
const PREDICATE_POLL_MS = 120;

/**
 * The step machine. Owns which guide is active, the phase (welcome → steps →
 * done), how the current step advances (Next / hotspot click / consumer event
 * / polled predicate), progress bookkeeping and the event stream hosts use.
 * Rendering is the outlet's job; keyboard handling too.
 *
 * Every state change emits on `changes$` (inside the Angular zone) so the
 * outlet can re-render.
 */
@Injectable({ providedIn: 'root' })
export class PrepTourService implements OnDestroy {
  private _active: PrepTourDefinition | null = null;
  private _index = 0;
  private _phase: PrepTourPhase | null = null;
  /** True while the current step's anchor is missing and `whenMissing: 'explain'`. */
  private _missing = false;
  private _completionCard: PrepTourDialogContent | null = null;
  private _minimized = false;
  private readonly _events = new Subject<PrepTourEvent>();
  private readonly _changes = new Subject<void>();
  private pollHandle: ReturnType<typeof setInterval> | null = null;
  private pollStep: PrepTourStep | null = null;
  private returnFocusTo: HTMLElement | null = null;
  private entering = 0;

  readonly events$: Observable<PrepTourEvent> = this._events.asObservable();
  /** Emits after every state change of the machine. */
  readonly changes$: Observable<void> = this._changes.asObservable();

  constructor(
    private readonly registry: PrepTourAnchorRegistry,
    private readonly guides: PrepGuideRegistry,
    private readonly progress: PrepTourProgressStore,
    private readonly zone: NgZone,
  ) {}

  ngOnDestroy(): void {
    this.clearPredicate();
  }

  // ── State ────────────────────────────────────────────────────────────

  get active(): PrepTourDefinition | null { return this._active; }
  get phase(): PrepTourPhase | null { return this._phase; }
  get stepIndex(): number { return this._index; }
  get stepCount(): number { return this._active ? this._active.steps.length : 0; }
  get step(): PrepTourStep | null {
    const g = this._active;
    return g && this._phase === 'step' ? (g.steps[this._index] || null) : null;
  }
  get anchorMissing(): boolean { return this._missing; }
  /** The current (minimisable) step is folded to its restore button. */
  get minimized(): boolean { return this._minimized; }
  /** The resolved completion card while `phase === 'done'`. */
  get completionCard(): PrepTourDialogContent | null { return this._completionCard; }
  get isFirst(): boolean { return this._index === 0; }
  get isLast(): boolean { return this._index >= this.stepCount - 1; }
  /** The current step advances by the Next/Finish button. */
  get canNext(): boolean {
    const s = this.step;
    return !!s && (!s.advance || s.advance.kind === 'next' || this._missing);
  }

  // ── Control ──────────────────────────────────────────────────────────

  /** Start a guide by definition or registered id. Invalid definitions throw. */
  start(guide: PrepTourDefinition | string, opts: PrepTourStartOptions = {}): void {
    const def = typeof guide === 'string' ? this.guides.get(guide) : guide;
    if (!def) throw new Error(`prep-tour: unknown guide "${String(guide)}"`);
    const v = validateTourDefinition(def);
    if (v.ok === false) throw new Error(`prep-tour: invalid guide "${def.id}": ${v.errors.join('; ')}`);
    if (this._active) this.stop('restart');

    this.returnFocusTo = opts.returnFocusTo || null;
    this._active = def;
    this.progress.markSeen(def.id);
    this.emit('start', def);
    if (def.welcome && !opts.skipWelcome && opts.fromStep === undefined) {
      this._phase = 'welcome';
      this._index = 0;
      this.changed();
      return;
    }
    const from = opts.fromStep === undefined ? 0 : opts.fromStep;
    void this.goTo(Math.min(Math.max(from, 0), def.steps.length - 1), 1);
  }

  /** From the welcome dialog: begin the steps. */
  begin(): void {
    if (this._phase !== 'welcome') return;
    void this.goTo(0, 1);
  }

  /** From the welcome dialog: "Skip for now" (remembered as dismissed). */
  dismiss(): void {
    const g = this._active;
    if (g) this.progress.dismiss(g.id);
    this.stop('dismissed');
  }

  /** Next / Finish. Ignored on action steps (they advance through their own condition). */
  next(): void {
    if (!this.canNext) return;
    const s = this.step;
    if (s && s.onNext && s.onNext() === false) {
      // The step stays (it iterates through its own items): re-measure the
      // (re-targeted) anchor and re-announce.
      const g = this._active;
      this.registry.reveal(s.target);
      this.registry.invalidate();
      if (g) this.emit('step', g, s);
      this.changed();
      return;
    }
    this.advance();
  }

  /** Jump to a step by id (branching hosts; no-op for unknown ids). */
  goToStep(id: string): void {
    const g = this._active;
    if (!g || this._phase !== 'step') return;
    const i = g.steps.findIndex((s) => s.id === id);
    if (i < 0) return;
    void this.goTo(i, i >= this._index ? 1 : -1);
  }

  back(): void {
    const g = this._active;
    if (!g || this._phase !== 'step' || this._index === 0) return;
    this.emit('back', g, this.step || undefined);
    void this.goTo(this._index - 1, -1);
  }

  /** Skip the current (action) step — or jump to `skipTo` ("Skip all"). */
  skip(): void {
    const g = this._active;
    if (!g || this._phase !== 'step') return;
    const s = this.step;
    this.emit('skip', g, s || undefined);
    const skipTo = this.skipTarget();
    if (skipTo === PREP_TOUR_END) { this.complete(); return; }
    if (skipTo) {
      const i = g.steps.findIndex((x) => x.id === skipTo);
      if (i >= 0) { void this.goTo(i, i >= this._index ? 1 : -1); return; }
    }
    if (this._index >= g.steps.length - 1) this.complete();
    else void this.goTo(this._index + 1, 1);
  }

  /** Fold the current step to its restore button (minimisable steps only). */
  minimize(): void {
    const s = this.step;
    if (s && s.minimizable) { this._minimized = true; this.changed(); }
  }

  /** Bring a minimised step back. */
  restore(): void {
    this._minimized = false;
    this.changed();
  }

  /** Pager arrow of the current step: move, then re-measure the (re-targeted) anchor. */
  pagerMove(direction: 'prev' | 'next'): void {
    const g = this._active;
    const s = this.step;
    const p = s ? s.pager : undefined;
    if (!g || !s || !p) return;
    if (direction === 'next') p.next(); else p.prev();
    this.registry.reveal(s.target);
    this.registry.invalidate();
    this.emit('step', g, s);
    this.changed();
  }

  /** Resolved "Skip all" target of the current step (functions evaluated now). */
  skipTarget(): string | undefined {
    const s = this.step;
    const v = s ? s.skipTo : undefined;
    return typeof v === 'function' ? v() : v;
  }

  /** Satisfies the current step when it waits for `{ kind: 'event', name }`. */
  signal(name: string): void {
    const s = this.step;
    if (s && s.advance && s.advance.kind === 'event' && s.advance.name === name) this.advance();
  }

  /** Hotspot click → satisfies `{ kind: 'click' }`. */
  hotspotClick(): void {
    const s = this.step;
    if (s && s.advance && s.advance.kind === 'click') this.advance();
  }

  /**
   * Hosts call this right after a state change the current step's predicate
   * or texts depend on: the predicate is re-checked at once (instead of on
   * the next poll) and the outlet re-renders.
   */
  notify(): void {
    if (!this.checkPredicate()) this.changed();
  }

  /** Stop without completing (Esc, ×, abort). */
  stop(reason = 'closed'): void {
    const g = this._active;
    if (!g) return;
    const s = this.step;
    if (s && this._phase === 'step') {
      this.progress.setLastStep(g.id, this._index);
      this.exitStep(s);
    }
    this.emit('abort', g, s || undefined, reason);
    this.teardown();
  }

  /** From the completion dialog. */
  finish(): void {
    if (!this._active) return;
    this.teardown();
  }

  // ── Internals ────────────────────────────────────────────────────────

  private advance(): void {
    const g = this._active;
    if (!g || this._phase !== 'step') return;
    const s = this.step;
    this.emit('advance', g, s || undefined);
    const branch = s ? (typeof s.next === 'function' ? s.next() : s.next) : undefined;
    if (branch === PREP_TOUR_END) { this.complete(); return; }
    if (branch) {
      const i = g.steps.findIndex((x) => x.id === branch);
      if (i >= 0) { void this.goTo(i, i >= this._index ? 1 : -1); return; }
    }
    if (this._index >= g.steps.length - 1) this.complete();
    else void this.goTo(this._index + 1, 1);
  }

  private complete(): void {
    const g = this._active;
    if (!g) return;
    const s = this.step;
    if (s) this.exitStep(s);
    this.progress.markCompleted(g.id, g.version === undefined ? 1 : g.version);
    this.emit('complete', g);
    const card = typeof g.completion === 'function' ? g.completion() : g.completion;
    if (card) {
      this._completionCard = card;
      this._phase = 'done';
      this.changed();
    } else {
      this.teardown();
    }
  }

  private async goTo(index: number, direction: 1 | -1): Promise<void> {
    const g = this._active;
    if (!g) return;
    const prev = this.step;
    if (prev) this.exitStep(prev);
    const token = ++this.entering;
    const step = g.steps[index];
    this._missing = false;

    // Anchor policy — resolved BEFORE onEnter so a hook can still open the
    // panel that would make the anchor appear (checked again after).
    let missing = !!step.target && !this.registry.has(step.target);
    if (step.onEnter) {
      try { await step.onEnter(); } catch { /* consumer hook failed — show the step anyway */ }
      if (token !== this.entering || this._active !== g) return;
      missing = !!step.target && !this.registry.has(step.target);
    }
    if (missing) {
      this.emit('anchor-missing', g, step);
      const policy = step.whenMissing || 'skip';
      if (policy === 'abort') { this.stop('anchor-missing'); return; }
      if (policy === 'skip') {
        const nextIndex = index + direction;
        if (nextIndex < 0) { this._phase = 'step'; this._index = index; this.changed(); return; }
        if (nextIndex >= g.steps.length) { this._index = index; this._phase = 'step'; this.complete(); return; }
        return this.goTo(nextIndex, direction);
      }
      this._missing = true;
    }

    this._index = index;
    this._phase = 'step';
    this._minimized = false;
    this.registry.reveal(step.target);
    this.registry.invalidate();
    this.emit('step', g, step);
    this.armPredicate(step);
    this.changed();
  }

  private armPredicate(step: PrepTourStep): void {
    this.clearPredicate();
    const adv = step.advance;
    if (!adv || adv.kind !== 'predicate' || this._missing) return;
    this.pollStep = step;
    this.zone.runOutsideAngular(() => {
      this.pollHandle = setInterval(() => {
        if (this.pollStep !== step || this.step !== step) { this.clearPredicate(); return; }
        let ok = false;
        try { ok = adv.satisfied(); } catch { ok = false; }
        if (ok) this.zone.run(() => { if (this.step === step) this.advance(); });
      }, PREDICATE_POLL_MS);
    });
  }

  /** Evaluate the armed predicate now; true when it advanced the guide. */
  private checkPredicate(): boolean {
    const step = this.pollStep;
    if (!step || this.step !== step) return false;
    const adv = step.advance;
    if (!adv || adv.kind !== 'predicate') return false;
    let ok = false;
    try { ok = adv.satisfied(); } catch { ok = false; }
    if (!ok) return false;
    this.advance();
    return true;
  }

  private clearPredicate(): void {
    if (this.pollHandle !== null) {
      clearInterval(this.pollHandle);
      this.pollHandle = null;
    }
    this.pollStep = null;
  }

  private exitStep(step: PrepTourStep): void {
    this.clearPredicate();
    try { if (step.onExit) step.onExit(); } catch { /* ignore consumer errors */ }
  }

  private teardown(): void {
    this.clearPredicate();
    this.entering++;
    this._active = null;
    this._phase = null;
    this._index = 0;
    this._missing = false;
    this._minimized = false;
    this._completionCard = null;
    const el = this.returnFocusTo;
    this.returnFocusTo = null;
    this.changed();
    // Deferred: the dialog / coach-mark is still in the DOM until the next
    // change detection; the launcher focus must land after that.
    if (el && typeof el.focus === 'function') {
      setTimeout(() => { if (el.isConnected) el.focus(); }, 0);
    }
  }

  private changed(): void {
    this._changes.next();
  }

  private emit(type: PrepTourEvent['type'], g: PrepTourDefinition, step?: PrepTourStep, reason?: string): void {
    this._events.next({
      type,
      guideId: g.id,
      stepId: step ? step.id : undefined,
      stepIndex: step ? g.steps.indexOf(step) : undefined,
      reason,
    });
  }
}
